import ClearIcon from '@mui/icons-material/Clear';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Badge,
  CircularProgress,
  Fade,
  IconButton,
  SxProps,
  TextField,
  Theme,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  getAdvFiltersBtnSx,
  getBadgeIconSx,
  getDefaultIconSx,
  getIconButtonSx,
  getSearchTextfieldSx,
} from 'components/Search/styles';
import { Fragment, memo, useCallback, useEffect, useMemo } from 'react';
import { useListContext } from 'react-admin';
import { useTranslation } from 'react-i18next';
import { hasLength } from 'utility/functions';
import { useSearchModalContext } from '../SearchModal';
import { GenericSearchItem } from 'components/Search/components/SearchItemsProvider';

/**
 * --------------------------------------------------------
 * Generic - Text Input Component
 * - Debounced text input for search modals
 * - Advanced filters button with count badge
 * --------------------------------------------------------
 */

export const SearchModalTextInputGeneric = memo(() => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { isFetching, isLoading } = useListContext<GenericSearchItem>();
  const {
    advFilterCount,
    textInputRef,
    textInputValue,
    setTextInputValue,
    dbTextInputValue,
    setFilters,
    queryTextKey,
    handleOpenAdvPopover,
  } = useSearchModalContext();

  const fetchingOrLoading = isFetching || isLoading;

  // Text input handlers
  const hasSearchText = hasLength(textInputValue);

  const handleTextInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setTextInputValue(newValue);
    },
    [setTextInputValue],
  );

  const handleTextInputClear = useCallback(() => {
    setTextInputValue('');
    textInputRef.current?.focus();
  }, [setTextInputValue, textInputRef]);

  // Update filter state on debounced text input changes
  useEffect(() => {
    setFilters((prev) => {
      const currentValue = prev?.[queryTextKey];
      // Prevent update on first mount without text... ('' vs undefined)
      if (dbTextInputValue === '' && currentValue === undefined) {
        return prev;
      }
      // Only update if value actually changed
      if (dbTextInputValue === currentValue) {
        return prev;
      }
      // Update query text filter
      return {
        ...prev,
        [queryTextKey]: dbTextInputValue,
      };
    });
  }, [dbTextInputValue, queryTextKey, setFilters]);

  // Styles...
  const searchTextFieldSx = useMemo(() => getSearchTextfieldSx(theme, false), [theme]);

  const advFiltersBtnSx: SxProps<Theme> = useMemo(() => {
    const hasAdvancedFilters = Boolean(advFilterCount > 0);
    return getAdvFiltersBtnSx(theme, hasAdvancedFilters);
  }, [theme, advFilterCount]);

  // Translation strings
  const strLoading = t('blox.general.loading');
  const strClear = t('blox.actions.clear');
  const strAdvFilters = t('blox.navigation.assets.advanced_filters');
  const strSearch = t('blox.search.label');
  const strSearchLabel = `${strSearch}...`;

  return (
    <Fragment>
      <TextField
        data-testid="search-modal-search-text-input"
        inputRef={textInputRef}
        size="small"
        autoComplete="off"
        value={textInputValue}
        onChange={handleTextInputChange}
        disabled={false}
        placeholder={strSearchLabel}
        variant="outlined"
        fullWidth={true}
        data-private={true}
        sx={searchTextFieldSx}
      />

      <Fade
        in={fetchingOrLoading}
        appear={true}
        unmountOnExit={true}
      >
        <CircularProgress
          size={14}
          thickness={3}
          color="primary"
          variant="indeterminate"
          title={strLoading}
        />
      </Fade>

      <Fade
        in={hasSearchText}
        appear={true}
        unmountOnExit={true}
      >
        <Tooltip title={strClear}>
          <IconButton
            sx={getIconButtonSx}
            onClick={handleTextInputClear}
            data-testid="search-modal-clear-input-btn"
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Fade>

      {/* Advanced Search Popover Button */}
      <Tooltip title={strAdvFilters}>
        <IconButton
          data-testid="search-modal-advanced-filters-btn"
          onClick={handleOpenAdvPopover}
          sx={advFiltersBtnSx}
        >
          <Badge
            badgeContent={advFilterCount}
            showZero={advFilterCount !== 0}
            color="primary"
            sx={getBadgeIconSx}
          >
            <TuneIcon sx={getDefaultIconSx} />
          </Badge>
        </IconButton>
      </Tooltip>
    </Fragment>
  );
});
SearchModalTextInputGeneric.displayName = 'SearchModalTextInputGeneric';
